import React, {useState} from "react";
import {Download, Loader2} from "lucide-react";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {agentsApi} from "api/agents";
import {Agent} from "types";
import {Dialog} from "components/dialog";
import {FormInput} from "components/fields/FormInput";

interface ExportAgentButtonProps {
  agent: Agent;
}

const ExportAgentButton: React.FC<ExportAgentButtonProps> = ({ agent }) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [password, setPassword] = useState("");

  const isExportDisabled = isExporting || agent.is_archived;

  const handleExportClick = () => {
    if (isExportDisabled) return;
    setIsDialogOpen(true);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const passwordToUse = password.trim() || undefined;
      const agentType = agent.agent_type;
      const blob = await agentsApi.export(agent.id, agentType, passwordToUse);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${agent.name || "agent"}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(
        t("agents.export.success", {
          defaultValue: "Agent exported successfully",
        })
      );
      setIsDialogOpen(false);
      setPassword("");
    } catch (error) {
      console.error("Failed to export agent:", error);
      toast.error(
        t("agents.export.error", { defaultValue: "Failed to export agent" })
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleDialogClose = () => {
    if (!isExporting) {
      setIsDialogOpen(false);
      setPassword("");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleExportClick}
        disabled={isExportDisabled}
        className={`flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold shadow-sm transition-all duration-200 ${
          isExportDisabled
            ? "cursor-not-allowed bg-gray-300 text-gray-500 opacity-60 dark:bg-gray-700 dark:text-gray-400"
            : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 dark:active:bg-blue-700"
        }`}
        title={t("agents.export.title", { defaultValue: "Export Agent" })}
      >
        <Download className="h-4 w-4" />
        {t("agents.export.button", { defaultValue: "Export" })}
      </button>

      <Dialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        onClose={handleDialogClose}
        header={t("agents.export.dialog.title", {
          defaultValue: "Export Agent",
        })}
        subHeader={t("agents.export.dialog.subtitle", {
          defaultValue:
            "Enter an optional password to protect the exported file",
        })}
        className="w-full max-w-md"
        outsideClickDisabled={isExporting}
      >
        <div className="space-y-6 p-2">
          <FormInput
            label={t("agents.export.dialog.passwordLabel", {
              defaultValue: "Password (Optional)",
            })}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("agents.export.dialog.passwordPlaceholder", {
              defaultValue: "Leave empty for no password",
            })}
            disabled={isExporting}
          />

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleDialogClose}
              disabled={isExporting}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {isExporting && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("agents.export.dialog.exportButton", {
                defaultValue: "Export",
              })}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ExportAgentButton;
